import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { StockResponse } from 'src/app/models/stock-response.model';
import { RequestPayload } from 'src/app/models/stock-search-payload.model';
import { DemoStockResponse } from '../models/demo-stock-response.model';
import { DemoStockRequest } from '../models/demo-stock-request.model';
import { CosyRequestModel } from '../models/cosy-request.model';
import { CosyUrlResponse } from '../models/cosy-response.model';
import {
  UploadFinanceDocModel,
  UploadFinanceDocResponse,
} from '../models/upload-finance-doc.model';
import {
  TestDriveRequestModel,
  TestDriveRequestResponse,
} from '../models/test-drive-request.model';
import {
  DemoInspectionRequest,
  DemoInspectionResponse,
} from '../models/request-inspection.model';
import {
  DemoQuoteRequest,
  DemoQuoteResponse,
} from '../models/request-quote.model';
import { ShowroomDocument } from '../models/showroom-capacity.model';
import {
  CampaignDetailsRequest,
  RecallCampaignResponse,
} from '../models/campaign-details.component';
import {
  InspectionDocRequest,
  InspectionDocResponse,
} from '../models/inspection-doc-request.model';
import { StocksheetRequest } from '../models/stocksheet-request.model';
import {
  ShowroomDocRequest,
  ShowroomDocResponse,
} from '../models/showroom-document.model';
import { AuthService } from './auth.service';
import {
  UploadLicenseDocsRequest,
  UploadLicenseDocsResponse,
} from '../models/upload-license-docs.model';

@Injectable({
  providedIn: 'root',
})
export class StockService {
  constructor(private readonly http: HttpClient,
              private readonly authService: AuthService) {}
    private getHeaders(): HttpHeaders {
        const token = this.authService.getAccessToken() || '';
        const host = window.location.hostname.toLowerCase();
        const domainExt = host.match(/\.(com|net)/)?.[0] || '';
        const domainStr = domainExt === '.com' ? 'com' : 'net';

        let headers = new HttpHeaders()
            .set('Authorization', `${token}`)
            .set('domain', domainStr);

        return headers;
    }
  getAvailableStock(requestPayload: RequestPayload): Observable<StockResponse> {
    const url = '/available-stock';
    return this.http.post<StockResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  getBlockedStock(requestPayload: RequestPayload): Observable<StockResponse> {
    const url = '/blocked-stock';
    return this.http.post<StockResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  getOTPStock(requestPayload: RequestPayload): Observable<StockResponse> {
    const url = '/so-stock';
    return this.http.post<StockResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  getQuoteStock(requestPayload: RequestPayload): Observable<StockResponse> {
    const url = '/quote-stock';
    return this.http.post<StockResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  getInboundStock(requestPayload: RequestPayload): Observable<StockResponse> {
    const url = '/inbound-stock';
    return this.http.post<StockResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  getOutboundStock(requestPayload: RequestPayload): Observable<StockResponse> {
    const url = '/outbound-stock';
    return this.http.post<StockResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  getRfsStock(requestPayload: RequestPayload): Observable<StockResponse> {
    const url = '/so-outbound-stock';
    return this.http.post<StockResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  getDemoStock(
    requestPayload: DemoStockRequest
  ): Observable<DemoStockResponse> {
    const url = '/demo-stock';
    return this.http.post<DemoStockResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  getTableCosyUrl(requestPayload: CosyRequestModel): Observable<CosyUrlResponse> {
    const url = '/table-cosy-url';
    return this.http.post<CosyUrlResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

    getBatchCosyUrls(vinList: string[]): Observable<{ [key: string]: string }> {
        const url = '/batch-table-cosy-url';
        const body = { vins: vinList };
        return this.http.post<{ [key: string]: string }>(url, body, { headers: this.getHeaders() });
    }

  downloadShowroomDoc(
    request: ShowroomDocRequest
  ): Observable<ShowroomDocResponse> {
    const url = '/showroom-doc';
    return this.http.post<ShowroomDocResponse>(url, request);
  }

  uploadDocument(
    requestPayload: UploadFinanceDocModel
  ): Observable<UploadFinanceDocResponse> {
    const url = '/upload-finance-docs';
    return this.http.post<UploadFinanceDocResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  viewDocument(
    vin: string,
    dspReference: string,
    dealerCode: string
  ): Observable<UploadFinanceDocResponse> {
    const url = `/view-finance-docs/${vin}/${dspReference}/${dealerCode}`;
    return this.http.get<UploadFinanceDocResponse>(url, { headers: this.getHeaders() });
  }

  updateMileage(
    requestPayload: TestDriveRequestModel
  ): Observable<TestDriveRequestResponse> {
    const url = '/deleteTestDrive';
    return this.http.post<TestDriveRequestResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  requestInspection(
    requestPayload: DemoInspectionRequest
  ): Observable<DemoInspectionResponse> {
    const url = '/request-inspection';
    return this.http.post<DemoInspectionResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  requestQuote(
    requestPayload: DemoQuoteRequest
  ): Observable<DemoQuoteResponse> {
    const url = '/request-quote';
    return this.http.post<DemoQuoteResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  defleetQuote(
    requestPayload: DemoQuoteRequest
  ): Observable<DemoQuoteResponse> {
    const url = '/defleet-quote';
    return this.http.post<DemoQuoteResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  getCampaignDetails(
    requestPayload: CampaignDetailsRequest
  ): Observable<RecallCampaignResponse> {
    const url = '/find-recall-campain';
    return this.http.post<RecallCampaignResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  getInspectionDoc(
    requestPayload: InspectionDocRequest
  ): Observable<InspectionDocResponse> {
    const url = '/download-inspection-doc';
    return this.http.post<InspectionDocResponse>(url, requestPayload, { headers: this.getHeaders() });
  }

  downloadStockSheet(requestPayload: StocksheetRequest): Observable<Blob> {
    const url = '/stock/stocksheet';
    const headers = new HttpHeaders({
      accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'Content-Type': 'application/json',
    });
    return this.http.post(url, requestPayload, {
      headers,
      responseType: 'blob',
    });
  }

  uploadLicenseDocs(request: UploadLicenseDocsRequest): Observable<UploadLicenseDocsResponse> {
    const url = '/stock/upload-license-doc';
    return this.http.post<UploadLicenseDocsResponse>(url, request, { headers: this.getHeaders() });
  }

}
